import { createConfig, http } from 'wagmi';
import { bsc, bscTestnet } from 'wagmi/chains';
import { injected } from 'wagmi/connectors/injected';
import { mock } from 'wagmi/connectors/mock';
import { walletConnect } from 'wagmi/connectors/walletConnect';
import { defineChain, type Chain } from 'viem';
import { CHAIN_ID, IS_LOCAL, RPC_URL, WALLETCONNECT_PROJECT_ID } from './env.ts';

export const localChain = defineChain({
  id: 31337,
  name: 'Local BSC',
  nativeCurrency: { name: 'BNB', symbol: 'BNB', decimals: 18 },
  rpcUrls: { default: { http: [RPC_URL] } },
});

export const CHAIN: Chain = CHAIN_ID === 56 ? bsc : CHAIN_ID === 97 ? bscTestnet : localChain;

/** End-to-end tests connect with this mock account (anvil #3) on the local chain only. */
export const E2E_ACCOUNT = '0x90F79bf6EB2c4f870365E785982E1f101E93b906' as const;
const e2eParam = typeof window !== 'undefined' ? new URLSearchParams(window.location.search).get('e2e') : null;
const e2e = IS_LOCAL && e2eParam !== null;
/** `?e2e=safe` connects as the local stack's Safe (anvil #1) for governance tests. */
export const E2E_SAFE = '0x70997970C51812dc3A010C7d01b50e0d17dc79C8' as const;
/** `?e2e=6` / `?e2e=7` connect as anvil #6 / #7 (funded by the local stack, used by no other test). */
const E2E_WALLETS: Record<string, `0x${string}`> = {
  safe: E2E_SAFE,
  '6': '0x976EA74026E726554dB657fA54763abd0C3a0aa9',
  '7': '0x14dC79964da2C08b23698B3D3cc7Ca32193d9955',
};

const origin = typeof window !== 'undefined' ? window.location.origin : 'https://syntra.finance';

/**
 * Browser-extension and in-app-browser wallets connect through `injected` (EIP-6963 names each one); every other
 * wallet (mobile apps by QR code or deep link, Binance Wallet, Safe{Wallet} for the admin pages) goes through
 * WalletConnect, whose modal lists all of them. Mainnet builds require the project id (vite.config.ts).
 */
const connectors = [
  injected({ shimDisconnect: true }),
  ...(WALLETCONNECT_PROJECT_ID
    ? [
        walletConnect({
          projectId: WALLETCONNECT_PROJECT_ID,
          showQrModal: true,
          metadata: {
            name: 'Syntra DAO',
            description: 'Bond, stake and grow on BNB Smart Chain',
            url: origin, // must be on the project's allowed domains in the Reown dashboard
            icons: [`${origin}/icon-512.png`],
          },
        }),
      ]
    : []),
  ...(e2e ? [mock({ accounts: [E2E_WALLETS[e2eParam ?? ''] ?? E2E_ACCOUNT], features: { reconnect: true } })] : []),
];

export const wagmiConfig = createConfig({
  chains: [CHAIN],
  connectors,
  multiInjectedProviderDiscovery: true, // EIP-6963: MetaMask, Trust, Binance Wallet, OKX… each listed by name
  transports: { [CHAIN.id]: http(RPC_URL) } as Record<number, ReturnType<typeof http>>,
});

declare module 'wagmi' {
  interface Register {
    config: typeof wagmiConfig;
  }
}
